(() => {
  if (customElements.get('otd-photo-slideshow')) return;
  class OtdPhotoSlideshow extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.choices = [...this.querySelectorAll('[data-go]')];
      if (this.slides.length < 2) return;
      this.index = 0;
      this.abort = new AbortController();
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.autoplay = this.dataset.autoplay === 'true';
      this.delay = Math.max(5000, Number(this.dataset.interval) || 8000);
      this.userPaused = false;
      this.hovered = false;
      this.inView = false;
      this.rotation = this.querySelector('[data-rotation]');
      this.status = this.querySelector('[data-status]');
      const listen = (target, name, fn) => target.addEventListener(name, fn, {signal:this.abort.signal});
      listen(this, 'click', event => {
        const button = event.target.closest('button');
        if (!button || !this.contains(button)) return;
        if (button.hasAttribute('data-go')) this.show(Number(button.dataset.go), true);
        if (button.hasAttribute('data-next')) this.show(this.index + 1, true);
        if (button.hasAttribute('data-prev')) this.show(this.index - 1, true);
        if (button.hasAttribute('data-rotation')) {
          this.userPaused = !this.userPaused;
          this.schedule();
        }
      });
      listen(this, 'keydown', event => {
        if (!event.target.closest('[data-go],[data-next],[data-prev]')) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          this.show(this.index + (event.key === 'ArrowRight' ? 1 : -1), true);
        }
      });
      listen(this, 'focusin', () => {this.userPaused = true; this.schedule();});
      listen(this, 'pointerenter', event => {if(event.pointerType === 'mouse'){this.hovered = true; this.schedule();}});
      listen(this, 'pointerleave', event => {if(event.pointerType === 'mouse'){this.hovered = false; this.schedule();}});
      listen(this, 'pointerdown', event => {
        this.start = event.pointerType !== 'mouse' && !event.target.closest('a,button') ? {x:event.clientX,y:event.clientY,id:event.pointerId} : null;
      });
      listen(this, 'pointerup', event => {
        if (!this.start || this.start.id !== event.pointerId) return;
        const dx = event.clientX - this.start.x, dy = event.clientY - this.start.y;
        this.start = null;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) this.show(this.index + (dx < 0 ? 1 : -1), true);
      });
      listen(this, 'pointercancel', () => {this.start = null;});
      listen(document, 'visibilitychange', () => this.schedule());
      listen(this.motion, 'change', () => this.schedule());
      // Shopify editor selection should always reveal the selected slide and stay there.
      listen(document, 'shopify:block:select', event => {
        const selected = this.slides.findIndex(slide => slide.dataset.blockId === event.detail?.blockId);
        if (selected >= 0) this.show(selected, true);
      });
      this.observer = new IntersectionObserver(entries => {this.inView = entries[0].isIntersecting; this.schedule();}, {threshold:.15});
      this.observer.observe(this);
      this.querySelector('[data-controls]').hidden = false;
      this.sync();
    }
    show(next, manual = false) {
      if (manual) this.userPaused = true;
      this.index = (next + this.slides.length) % this.slides.length;
      this.sync();
      if (manual) this.status.textContent = this.slides[this.index].getAttribute('aria-label');
      this.schedule();
    }
    sync() {
      this.slides.forEach((slide, index) => {
        const active = index === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.inert = !active;
      });
      this.choices.forEach((choice,index) => {
        choice.classList.toggle('is-current',index === this.index);
        if(index === this.index) choice.setAttribute('aria-current','true');
        else choice.removeAttribute('aria-current');
      });
    }
    schedule() {
      clearTimeout(this.timer);
      this.timer = null;
      const permitted = this.autoplay && !this.userPaused && !this.motion.matches;
      this.rotation.textContent = this.userPaused ? 'Start slideshow' : 'Pause slideshow';
      this.rotation.hidden = !this.autoplay || this.motion.matches;
      this.dataset.playback = !this.autoplay ? 'disabled' : this.motion.matches ? 'reduced-motion' : this.userPaused ? 'paused' : this.hovered ? 'hover' : !this.inView ? 'offscreen' : document.hidden ? 'background' : 'playing';
      if (!permitted || this.hovered || !this.inView || document.hidden) return;
      this.timer = setTimeout(() => this.show(this.index + 1), this.delay);
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.observer?.disconnect();
      this.abort?.abort();
      this.abort = null;
    }
  }
  customElements.define('otd-photo-slideshow',OtdPhotoSlideshow);
})();
